import React, { useState, useRef, useEffect } from 'react';
import {
  PlusIcon,
  UserPlusIcon,
  DocumentPlusIcon,
  PhoneIcon,
  EnvelopeIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  FolderPlusIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface FABAction {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
  hoverColor: string;
  onClick: () => void;
}

interface FloatingActionButtonProps {
  actions: FABAction[];
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  size?: 'sm' | 'md' | 'lg';
}

const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({
  actions,
  position = 'bottom-right',
  size = 'md'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const fabRef = useRef<HTMLDivElement>(null);

  // Close FAB when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (fabRef.current && !fabRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const positionClasses = {
    'bottom-right': 'bottom-4 right-4 sm:bottom-6 sm:right-6',
    'bottom-left': 'bottom-4 left-4 sm:bottom-6 sm:left-6',
    'top-right': 'top-4 right-4 sm:top-6 sm:right-6',
    'top-left': 'top-4 left-4 sm:top-6 sm:left-6'
  };

  const sizeClasses = {
    sm: 'w-8 h-8 sm:w-10 sm:h-10',
    md: 'w-10 h-10 sm:w-12 sm:h-12',
    lg: 'w-12 h-12 sm:w-14 sm:h-14'
  };

  const iconSizeClasses = {
    sm: 'h-3 w-3 sm:h-4 sm:w-4',
    md: 'h-4 w-4 sm:h-5 sm:w-5',
    lg: 'h-5 w-5 sm:h-6 sm:w-6'
  };

  const actionSizeClasses = {
    sm: 'w-6 h-6 sm:w-8 sm:h-8',
    md: 'w-8 h-8 sm:w-10 sm:h-10',
    lg: 'w-10 h-10 sm:w-12 sm:h-12'
  };

  const handleMainClick = () => {
    if (actions.length === 1) {
      actions[0].onClick();
    } else {
      setIsOpen(!isOpen);
    }
  };

  const handleActionClick = (action: FABAction) => {
    action.onClick();
    setIsOpen(false);
  };

  return (
    <div 
      ref={fabRef}
      className={`fixed ${positionClasses[position]} z-[9999] flex flex-col items-center`}
      role="group"
      aria-label="Floating action menu"
    >
      {/* Backdrop blur when open */}
      {isOpen && actions.length > 1 && (
        <div 
          className="fixed inset-0 bg-black/10 backdrop-blur-[1px] -z-10"
          style={{ 
            animation: 'fadeIn 0.2s ease-out forwards'
          }}
        />
      )}

      {/* Action Buttons */}
      {isOpen && actions.length > 1 && (
        <div className={`flex flex-col items-center space-y-2 sm:space-y-3 mb-3 sm:mb-4 ${
          position.includes('top') ? 'flex-col-reverse' : ''
        }`}>
          {actions.map((action, index) => (
            <div
              key={action.id}
              className="group relative"
              style={{
                animationDelay: `${index * 60}ms`,
                animation: isOpen ? 'fabActionSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' : 'none'
              }}
            >
              {/* Action Label with Glassmorphism */}
              <div className={`absolute ${
                position.includes('right') ? 'right-full mr-3 sm:mr-4' : 'left-full ml-3 sm:ml-4'
              } top-1/2 transform -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md text-gray-900 dark:text-white px-3 py-2 rounded-xl text-xs sm:text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-lg border border-white/20 dark:border-gray-700/20 scale-95 group-hover:scale-100`}>
                {action.label}
                {/* Modern arrow with glassmorphism */}
                <div className={`absolute top-1/2 transform -translate-y-1/2 ${
                  position.includes('right') 
                    ? 'left-full -ml-1 border-l-white/90 dark:border-l-gray-800/90' 
                    : 'right-full -mr-1 border-r-white/90 dark:border-r-gray-800/90'
                } w-0 h-0 border-t-4 border-b-4 border-t-transparent border-b-transparent ${
                  position.includes('right') ? 'border-l-8' : 'border-r-8'
                }`}></div>
              </div>
              
              {/* Action Button with enhanced glassmorphism */}
              <button
                onClick={() => handleActionClick(action)}
                className={`${actionSizeClasses[size]} relative overflow-hidden text-white rounded-full shadow-lg transform transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center group-hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-primary-500/30`}
                style={{
                  background: `linear-gradient(135deg, ${action.color}f0, ${action.color}cc)`,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
                aria-label={action.label}
              >
                {/* Glassmorphism overlay */}
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full" />
                
                {/* Ripple effect */}
                <div className="absolute inset-0 rounded-full opacity-0 group-active:opacity-100 bg-white/20 animate-ping" />
                
                <action.icon className={`${iconSizeClasses[size]} relative z-10 drop-shadow-sm`} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Main FAB Button with enhanced design */}
      <button
        onClick={handleMainClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`${sizeClasses[size]} relative overflow-hidden bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 hover:from-primary-600 hover:via-primary-700 hover:to-primary-800 text-white rounded-full shadow-xl transform transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center focus:outline-none focus:ring-4 focus:ring-primary-500/30 group`}
        style={{
          boxShadow: isOpen || isHovered
            ? '0 12px 40px rgba(139, 92, 246, 0.4), 0 4px 16px rgba(139, 92, 246, 0.3)' 
            : '0 8px 25px rgba(139, 92, 246, 0.3), 0 3px 10px rgba(139, 92, 246, 0.2)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
        aria-label={actions.length === 1 ? actions[0].label : isOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={actions.length > 1 ? isOpen : undefined}
      >
        {/* Animated background gradient */}
        <div className={`absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full`} />
        
        {/* Pulse effect when hovered */}
        <div className={`absolute inset-0 rounded-full bg-primary-400/30 ${isHovered ? 'animate-ping' : ''}`} />
        
        {/* Icon with smooth transitions */}
        <div className="relative z-10">
          {isOpen && actions.length > 1 ? (
            <XMarkIcon 
              className={`${iconSizeClasses[size]} transform transition-all duration-300 ${isOpen ? 'rotate-90 scale-110' : ''} drop-shadow-sm`} 
            />
          ) : (
            <PlusIcon 
              className={`${iconSizeClasses[size]} transform transition-all duration-300 ${isOpen ? 'rotate-45 scale-110' : ''} drop-shadow-sm`} 
            />
          )}
        </div>

        {/* Ripple effect on click */}
        <div className="absolute inset-0 rounded-full opacity-0 group-active:opacity-100 bg-white/20 animate-ping" />
      </button>

      {/* Accessibility: Screen reader text */}
      <div className="sr-only">
        {actions.length > 1 && isOpen && (
          <div>
            Menu opened. {actions.length} actions available. Press Escape to close.
          </div>
        )}
      </div>
    </div>
  );
};

// Enhanced CSS animations with modern easing
const styles = `
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fabActionSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.8) rotate(-10deg);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1) rotate(0deg);
  }
}

/* Smooth scrollbar for better UX */
.fab-container::-webkit-scrollbar {
  width: 2px;
}

.fab-container::-webkit-scrollbar-track {
  background: transparent;
}

.fab-container::-webkit-scrollbar-thumb {
  background: rgba(139, 92, 246, 0.3);
  border-radius: 1px;
}

/* Enhanced focus styles for accessibility */
.fab-button:focus-visible {
  outline: 2px solid #8b5cf6;
  outline-offset: 2px;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .fab-button,
  .fab-action {
    animation: none !important;
    transition: none !important;
  }
}
`;

// Inject styles with better error handling
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('fab-styles');
  if (!existingStyle) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'fab-styles';
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
  }
}

export default FloatingActionButton;

// Enhanced predefined action configurations with modern colors
export const FABActions = {
  addDeal: {
    id: 'add-deal',
    label: 'Új ügylet',
    icon: ChatBubbleLeftRightIcon,
    color: '#10B981',
    bgColor: 'bg-emerald-500',
    hoverColor: 'hover:bg-emerald-600'
  },
  addCustomer: {
    id: 'add-customer',
    label: 'Új ügyfél',
    icon: UserPlusIcon,
    color: '#3B82F6',
    bgColor: 'bg-blue-500',
    hoverColor: 'hover:bg-blue-600'
  },
  addProject: {
    id: 'add-project',
    label: 'Új projekt',
    icon: FolderPlusIcon,
    color: '#8B5CF6',
    bgColor: 'bg-violet-500',
    hoverColor: 'hover:bg-violet-600'
  },
  addTask: {
    id: 'add-task',
    label: 'Új feladat',
    icon: CheckCircleIcon,
    color: '#F59E0B',
    bgColor: 'bg-amber-500',
    hoverColor: 'hover:bg-amber-600'
  },
  addDocument: {
    id: 'add-document',
    label: 'Új dokumentum',
    icon: DocumentPlusIcon,
    color: '#6366F1',
    bgColor: 'bg-indigo-500',
    hoverColor: 'hover:bg-indigo-600'
  },
  makeCall: {
    id: 'make-call',
    label: 'Hívás',
    icon: PhoneIcon,
    color: '#EF4444',
    bgColor: 'bg-red-500',
    hoverColor: 'hover:bg-red-600'
  },
  sendEmail: {
    id: 'send-email',
    label: 'Email',
    icon: EnvelopeIcon,
    color: '#F59E0B',
    bgColor: 'bg-amber-500',
    hoverColor: 'hover:bg-amber-600'
  }
};